import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Wraps leading manual labels in <strong>: "Step 1:", "1. Title:", "a. Item:", "ii. Item:".
 * Only trusted static manual strings should be passed through; output is escaped then marked safe.
 */
export function manualBoldLabelsHtml(text: string): string {
  if (!text) {
    return '';
  }
  const lead = text.match(/^\s*/)?.[0] ?? '';
  const body = text.slice(lead.length);

  const step = /^(Step\s+\d+\s*:)/i.exec(body);
  if (step) {
    const p = step[1];
    return escapeHtml(lead) + '<strong>' + escapeHtml(p) + '</strong>' + escapeHtml(body.slice(p.length));
  }

  const digitLabel = /^(\d+\.\s[^:]+:)/.exec(body);
  if (digitLabel) {
    const p = digitLabel[1];
    return escapeHtml(lead) + '<strong>' + escapeHtml(p) + '</strong>' + escapeHtml(body.slice(p.length));
  }

  const digitDash = /^(\d+\.\s.+?\s-\s)/.exec(body);
  if (digitDash) {
    const p = digitDash[1];
    return escapeHtml(lead) + '<strong>' + escapeHtml(p) + '</strong>' + escapeHtml(body.slice(p.length));
  }

  const romanLabel = /^(i{1,3}|iv|vi{0,3}|ix|v|x)\.\s[^:]+:/i.exec(body);
  if (romanLabel) {
    const p = romanLabel[0];
    return escapeHtml(lead) + '<strong>' + escapeHtml(p) + '</strong>' + escapeHtml(body.slice(p.length));
  }

  const letterLabel = /^([a-z]\.\s[^:]+:)/i.exec(body);
  if (letterLabel) {
    const p = letterLabel[1];
    return escapeHtml(lead) + '<strong>' + escapeHtml(p) + '</strong>' + escapeHtml(body.slice(p.length));
  }

  return escapeHtml(text);
}

@Pipe({
  name: 'manualBoldLabels',
  standalone: true,
})
export class ManualBoldLabelsPipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);

  transform(value: string | null | undefined): SafeHtml {
    if (value == null || value === '') {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    return this.sanitizer.bypassSecurityTrustHtml(manualBoldLabelsHtml(value));
  }
}
